/**
 * The event side of the worklist + events page (GDN-2774): one encounter's
 * event stream, the badge that summarises it in the worklist row, and the
 * controls that filter both.
 *
 * Badge and stream read the same cached probe (see `useEncounterEvents`), so
 * they can never disagree — the badge is the stream's headline.
 *
 * The load-bearing state here is the EMPTY one. A worklist row asserts an
 * encounter is in some state; if nothing was ever recorded about it, that
 * state came from somewhere other than the event stream, and this page exists
 * to make that obvious. So "no events" is not a muted em-dash — it's the
 * loudest thing on the row.
 *
 * Which is exactly why the tag filter (GDN-2776) is kept away from that claim.
 * "No events recorded" is a statement about coverage; "no billing-tagged
 * events" is a statement about a filter. They look identical in a table and
 * mean completely different things, so the two are measured separately and
 * rendered differently — see `EncounterEventCoverageChip`.
 */
import { Fragment, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

import type { RecentActivityEntry } from '../store/api';
import {
  EVENT_PROBE_LIMIT,
  useEncounterEvents,
  useEventPayload,
  type EventLens,
} from '../hooks/useEncounterEvents';
import { CopyIconButton } from './copy-icon-button';
import { EventActor } from './event-actor';

const LENSES: EventLens[] = ['all', 'domain', 'audit', 'usage'];

const LENS_LABELS: Record<EventLens, string> = {
  all: 'All',
  domain: 'Domain',
  audit: 'Audit',
  usage: 'Usage',
};

/** Columns in the stream table, including the expander. */
const STREAM_COLUMN_COUNT = 5;

const formatDateTime = (iso: string): string => {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleString();
};

const shortId = (id: string): string => (id.length > 12 ? `${id.slice(0, 12)}…` : id);

/** The lens toggle, hoisted so the page can drive every row from one control. */
export const EventLensToggle = ({
  lens,
  onChange,
}: {
  lens: EventLens;
  onChange: (next: EventLens) => void;
}) => (
  <ToggleButtonGroup
    size="small"
    exclusive
    value={lens}
    onChange={(_, next: EventLens | null) => {
      if (next) onChange(next);
    }}
    aria-label="Event lens"
  >
    {LENSES.map(value => (
      <ToggleButton key={value} value={value}>
        {LENS_LABELS[value]}
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
);

/**
 * Narrows the event panes to the `billing` catalog tag — jotbill's
 * revenue-cycle lifecycle. Sits beside the lens toggle because it composes with
 * it: the tag and the envelope-flag lens are ANDed server-side.
 */
export const BillingEventsToggle = ({
  active,
  onChange,
}: {
  active: boolean;
  onChange: (next: boolean) => void;
}) => (
  <Tooltip title="Show only events tagged for jotbill's revenue cycle. Coverage badges stay unfiltered.">
    <Chip
      size="small"
      icon={<ReceiptLongIcon />}
      label="Billing events"
      color={active ? 'primary' : 'default'}
      variant={active ? 'filled' : 'outlined'}
      onClick={() => onChange(!active)}
      aria-pressed={active}
      data-testid="billing-events-toggle"
    />
  </Tooltip>
);

/** What a row's coverage probe resolved to. */
export type CoverageState = 'loading' | 'empty' | 'present' | 'unknown';

/**
 * How many of a row's events carry the active tag.
 *
 * Rendered beside the coverage badge, never instead of it, and deliberately
 * quiet: zero here is a filter result. The loud red badge next to it is the one
 * making a claim about whether anything was recorded at all.
 */
const TaggedEventCountChip = ({
  organizationId,
  encounterId,
  lens,
  tag,
}: {
  organizationId: string;
  encounterId: string;
  lens: EventLens;
  tag: string;
}) => {
  const { count, hasMore, isLoading, isDenied, isUnknownTag, isFailed } = useEncounterEvents({
    organizationId,
    encounterId,
    lens,
    tag,
  });

  // Nothing useful to add on a refusal: the coverage badge beside this one
  // already reports the PHI gate and store failures, and an uncurated tag is
  // announced once at the page level rather than 25 times down the column.
  if (isLoading || isDenied || isUnknownTag || isFailed) return null;

  return (
    <Chip
      size="small"
      variant="outlined"
      color={count === 0 ? 'default' : 'info'}
      label={count === 0 ? `No ${tag} events` : `${count}${hasMore ? '+' : ''} ${tag}`}
      data-testid="tagged-event-count"
    />
  );
};

/**
 * The worklist row's coverage badge. Red and explicit when the encounter has
 * no events under the current lens; quiet otherwise.
 *
 * Reports its verdict up through `onResolve` so the page can count gaps across
 * the visible rows without duplicating the probe.
 *
 * The coverage probe is UNTAGGED even when a tag filter is active, and that is
 * the whole point: this badge, the page banner and the gaps-only filter all
 * answer "was anything ever recorded for this encounter?". Passing the tag
 * through would silently change the question to "was anything recorded that
 * carries this tag?" while the UI kept saying "No events recorded" — a claim
 * about instrumentation coverage, made on the strength of a filter. The tag
 * gets its own chip, and its own words.
 */
export const EncounterEventCoverageChip = ({
  organizationId,
  encounterId,
  lens,
  tag,
  onResolve,
}: {
  organizationId: string;
  encounterId: string;
  lens: EventLens;
  /** Active catalog filter, reported alongside coverage — never folded into it. */
  tag?: string;
  onResolve?: (encounterId: string, state: CoverageState) => void;
}) => {
  const { count, hasMore, isLoading, isEmpty, isDenied, isFailed } = useEncounterEvents({
    organizationId,
    encounterId,
    lens,
  });

  const state: CoverageState = isLoading
    ? 'loading'
    : isDenied || isFailed
      ? 'unknown'
      : isEmpty
        ? 'empty'
        : 'present';

  useEffect(() => {
    onResolve?.(encounterId, state);
  }, [onResolve, encounterId, state]);

  let coverage;
  if (isLoading) {
    coverage = <CircularProgress size={14} aria-label="Checking event coverage" />;
  } else if (isDenied) {
    coverage = <Chip size="small" variant="outlined" color="warning" label="PHI gated" />;
  } else if (isFailed) {
    coverage = (
      <Tooltip title="The activity store did not answer — coverage is unknown, not absent.">
        <Chip size="small" variant="outlined" color="default" label="Unknown" />
      </Tooltip>
    );
  } else if (isEmpty) {
    coverage = (
      <Chip
        size="small"
        color="error"
        icon={<ErrorOutlineIcon />}
        label="No events recorded"
        sx={{ fontWeight: 700 }}
      />
    );
  } else {
    coverage = (
      <Chip
        size="small"
        variant="outlined"
        color="success"
        label={hasMore ? `${count}+ events` : `${count} event${count === 1 ? '' : 's'}`}
      />
    );
  }

  // Only rows that HAVE events gain a tagged chip. On a row with none, "no
  // billing events" is a weaker restatement of the red badge beside it, and
  // pairing the two is how a reader starts reading them as the same claim.
  if (!tag || state !== 'present') return coverage;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
      {coverage}
      <TaggedEventCountChip
        organizationId={organizationId}
        encounterId={encounterId}
        lens={lens}
        tag={tag}
      />
    </Box>
  );
};

/**
 * One event's payload, fetched only once its row is expanded.
 *
 * Rendered as formatted JSON rather than a field table on purpose: these are
 * arbitrary event envelopes with no shared shape, and the question being asked
 * of them ("what did the producer actually record here?") is answered by the
 * raw document, not by a curated subset of it.
 */
const EventPayloadPanel = ({
  organizationId,
  eventId,
}: {
  organizationId: string;
  eventId: string;
}) => {
  const { data, isMissing, isLoading, isDenied, isFailed } = useEventPayload({
    organizationId,
    eventId,
  });

  if (isLoading) {
    return (
      <Box sx={{ py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">
          Loading payload…
        </Typography>
      </Box>
    );
  }

  if (isDenied) {
    return (
      <Alert severity="warning" sx={{ my: 1 }}>
        Requires admin PHI access to read this event&apos;s payload.
      </Alert>
    );
  }

  if (isFailed) {
    return (
      <Alert severity="warning" sx={{ my: 1 }} data-testid="event-payload-failed">
        This event&apos;s payload could not be loaded. The event itself is still recorded — only the
        payload read failed.
      </Alert>
    );
  }

  if (isMissing) {
    return (
      <Alert severity="info" sx={{ my: 1 }}>
        The activity store no longer holds this event, so its payload cannot be shown.
      </Alert>
    );
  }

  if (!data || Object.keys(data).length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1.5 }}>
        This event carries no payload — it was recorded as an envelope only.
      </Typography>
    );
  }

  const json = JSON.stringify(data, null, 2);

  return (
    <Box sx={{ py: 1 }} data-testid="event-payload">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="overline" color="text.secondary">
          Payload
        </Typography>
        <CopyIconButton value={json} label="event payload" />
      </Box>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1.5,
          maxHeight: 320,
          overflow: 'auto',
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'action.hover',
          fontFamily: 'monospace',
          fontSize: 12,
          lineHeight: 1.6,
        }}
      >
        {json}
      </Box>
    </Box>
  );
};

/** One row of the stream, expandable into the event's payload. */
const EventStreamRow = ({
  organizationId,
  entry,
}: {
  organizationId: string;
  entry: RecentActivityEntry;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Fragment>
      <TableRow
        hover
        onClick={() => setIsOpen(open => !open)}
        sx={{ cursor: 'pointer' }}
        data-testid="event-row"
      >
        <TableCell padding="checkbox">
          {/* Named apart from the worklist row's Expand/Collapse: both live in
              the same table when a row is open, and identical labels would make
              the two levels indistinguishable to a screen reader. */}
          <IconButton size="small" aria-label={isOpen ? 'Hide payload' : 'Show payload'}>
            {isOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(entry.occurred_at)}</TableCell>
        <TableCell>
          <Chip
            size="small"
            variant="outlined"
            color={entry.is_access ? 'default' : 'info'}
            label={entry.event_type}
          />
        </TableCell>
        <TableCell>
          <EventActor actor={entry.actor} onBehalfOf={entry.on_behalf_of} />
        </TableCell>
        <TableCell>
          {entry.subject_type ? (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
              <Typography variant="body2">{entry.subject_type}</Typography>
              {entry.resource_id && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: 'monospace' }}
                >
                  {shortId(entry.resource_id)}
                </Typography>
              )}
            </Box>
          ) : (
            '—'
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          colSpan={STREAM_COLUMN_COUNT}
          sx={{ py: 0, borderBottom: isOpen ? undefined : 'none' }}
        >
          {/* unmountOnExit is what makes the payload lazy: the fetch hook lives
              inside, so a collapsed row has never asked for its payload. */}
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <EventPayloadPanel organizationId={organizationId} eventId={entry.event_id} />
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};

/** The expanded panel: this encounter's stream, newest first. */
export const EncounterEventStream = ({
  organizationId,
  encounterId,
  lens,
  tag,
}: {
  organizationId: string;
  encounterId: string;
  lens: EventLens;
  /** Catalog tag to narrow the stream to. Omit for the unfiltered timeline. */
  tag?: string;
}) => {
  const { entries, hasMore, isLoading, isEmpty, isDenied, isUnknownTag, isFailed } =
    useEncounterEvents({ organizationId, encounterId, lens, tag });

  // The coverage question, asked the way the row badge asks it: UNFILTERED.
  //
  // An empty filtered stream and a real coverage gap are indistinguishable from
  // the tagged query alone — an encounter with no events at all returns nothing
  // under any tag, because there is nothing to filter. Only the unfiltered
  // answer separates "the filter excluded everything" from "nothing was ever
  // recorded", and this panel is not allowed to claim the former without it.
  //
  // Skipped when no tag is active (the query above is already the unfiltered
  // one), and otherwise free: the row's coverage badge issued this exact query,
  // so RTK Query serves it from cache.
  const coverage = useEncounterEvents({ organizationId, encounterId, lens, skip: !tag });

  if (isLoading) {
    return (
      <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (isDenied) {
    return (
      <Alert severity="warning" sx={{ my: 1 }}>
        Requires admin PHI access to read this encounter&apos;s events.
      </Alert>
    );
  }

  if (isUnknownTag) {
    return (
      <Alert severity="info" sx={{ my: 1 }} data-testid="stream-tag-not-curated">
        The <code>{tag}</code> tag is not curated in this environment, so the stream cannot be
        filtered by it. Turn the filter off to read the unfiltered timeline.
      </Alert>
    );
  }

  if (isFailed) {
    return (
      <Alert severity="warning" sx={{ my: 1 }}>
        The activity store did not answer. Event coverage for this encounter is unknown — this is
        not the same as having no events.
      </Alert>
    );
  }

  // An empty FILTERED stream says nothing about coverage, so it must not borrow
  // the coverage gap's words or its colour — but it only earns the filter's
  // words once the unfiltered probe above says there was something to filter.
  // A gap row expanded under an active filter falls through to the gap alert.
  if (isEmpty && tag) {
    if (coverage.isLoading) {
      return (
        <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={22} />
        </Box>
      );
    }

    if (coverage.isDenied || coverage.isFailed) {
      return (
        <Alert severity="warning" sx={{ my: 1 }} data-testid="tagged-coverage-unknown">
          <Typography sx={{ fontWeight: 600 }}>No {tag}-tagged events</Typography>
          <Typography variant="body2">
            Nothing in this encounter&apos;s {LENS_LABELS[lens].toLowerCase()} timeline carries the{' '}
            <code>{tag}</code> tag. Whether anything was recorded at all is unknown — the unfiltered
            check did not answer — so this is not evidence either way about coverage.
          </Typography>
        </Alert>
      );
    }

    if (!coverage.isEmpty) {
      return (
        <Alert severity="info" sx={{ my: 1 }} data-testid="no-tagged-events">
          <Typography sx={{ fontWeight: 600 }}>No {tag}-tagged events</Typography>
          <Typography variant="body2">
            Nothing in this encounter&apos;s {LENS_LABELS[lens].toLowerCase()} timeline carries the{' '}
            <code>{tag}</code> tag. That is a filter result, not a coverage gap — the row&apos;s
            Events badge and the page&apos;s gap count are measured unfiltered, so read those for
            whether anything was recorded at all.
          </Typography>
        </Alert>
      );
    }
  }

  if (isEmpty) {
    return (
      <Alert
        severity="error"
        icon={<ErrorOutlineIcon />}
        sx={{ my: 1 }}
        data-testid="no-events-recorded"
      >
        <Typography sx={{ fontWeight: 700 }}>No events recorded</Typography>
        <Typography variant="body2">
          Nothing was ever written to the {LENS_LABELS[lens].toLowerCase()} timeline for this
          encounter, yet the worklist places it in a concrete state. That state was derived from
          table reads, not from events — an event-coverage gap.
          {tag ? (
            <>
              {' '}
              The <code>{tag}</code> filter is on but is not why this is empty: the unfiltered
              timeline is empty too.
            </>
          ) : null}
        </Typography>
      </Alert>
    );
  }

  return (
    <Box sx={{ py: 1 }}>
      <Table size="small" aria-label="Encounter event stream">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" />
            <TableCell>When</TableCell>
            <TableCell>Event</TableCell>
            <TableCell>Actor</TableCell>
            <TableCell>Resource</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map(entry => (
            <EventStreamRow key={entry.event_id} organizationId={organizationId} entry={entry} />
          ))}
        </TableBody>
      </Table>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {hasMore
          ? `Showing the ${EVENT_PROBE_LIMIT} most recent events. This view measures coverage, not history — open the organization's Activity tab for the full timeline. Expand a row for its payload.`
          : 'Expand a row for the event’s full payload.'}
      </Typography>
    </Box>
  );
};
